import {
	CONNECTION_TYPE,
	EVALUATION_TYPE,
	IChooseOperation,
	IConnectionOperation,
	IOperation,
	OPERATION_TYPE,
} from '../../operations/models/operations.models';
import { IWorkflowDetailResponse } from '../models/workflows.models';

/**
 * Utilidades CONNECTIONS (evaluación financiera) del editor (NIF-530).
 *
 * Abrir cuando: mapear tipo UI verify/scoring o localizar conexiones con `evaluation` / CHOOSE.
 * Sin dependencias Angular — puro y testeable.
 */

/**
 * UI del form: `MATCH_DATA` → `verify`; resto → `scoring`.
 *
 * @param evaluation - `EVALUATION_TYPE` del API
 */
export function resolveFinancialEvalUiType(
	evaluation: EVALUATION_TYPE | string | null | undefined,
): 'verify' | 'scoring' {
	return evaluation === EVALUATION_TYPE.MATCH_DATA ? 'verify' : 'scoring';
}

/**
 * CONNECTIONS con evaluation (excluye SCRIPTURE).
 *
 * @param detail - Detalle del workflow
 */
export function findEvaluatedConnections(
	detail: Pick<IWorkflowDetailResponse, 'operations'> | undefined,
): IConnectionOperation[] {
	return (
		(detail?.operations?.filter(
			(op: IOperation) =>
				op.type === OPERATION_TYPE.CONNECTIONS &&
				(op as IConnectionOperation).config?.type !== CONNECTION_TYPE.SCRIPTURE &&
				Boolean((op as IConnectionOperation).config?.evaluation),
		) as IConnectionOperation[]) ?? []
	);
}

/**
 * Operación CHOOSE del detalle (si existe).
 *
 * @param detail - Detalle del workflow
 */
export function findChooseOperation(
	detail: Pick<IWorkflowDetailResponse, 'operations'> | undefined,
): IChooseOperation | undefined {
	return detail?.operations?.find((op: IOperation) => op.type === OPERATION_TYPE.CHOOSE) as
		| IChooseOperation
		| undefined;
}

/**
 * Conexiones con evaluation anidadas en CHOOSE.
 *
 * @param choose - Operación CHOOSE
 */
export function findEvaluatedConnectionsInChoose(
	choose: IChooseOperation | undefined,
): IConnectionOperation[] {
	return (
		(choose?.config?.operations?.filter(
			(op: IOperation) =>
				op.type === OPERATION_TYPE.CONNECTIONS && (op as IConnectionOperation).config?.evaluation,
		) as IConnectionOperation[]) ?? []
	);
}

/**
 * Patch del form CONNECTIONS a partir del detalle.
 * Sin conexiones evaluadas ni CHOOSE → `{ available: false }`.
 *
 * @param detail - Detalle del workflow
 */
export function mapConnectionsFormPatch(
	detail: Pick<IWorkflowDetailResponse, 'operations'> | undefined,
): Record<string, unknown> {
	const connectionsOperation = findEvaluatedConnections(detail);
	const choose = findChooseOperation(detail);
	const connectionsInChoose = findEvaluatedConnectionsInChoose(choose);
	const bankConnections = connectionsOperation.find(
		(c: IConnectionOperation) => c.config.type === CONNECTION_TYPE.BANK_SCORING,
	);

	if (!connectionsOperation.length && !connectionsInChoose.length) {
		return { available: false };
	}

	return {
		available: true,
		type: resolveFinancialEvalUiType(bankConnections?.config?.evaluation),
		connections:
			(connectionsOperation.length
				? connectionsOperation.map((c: IConnectionOperation) => c.config.type)
				: null) ||
			(connectionsInChoose.length
				? connectionsInChoose.map((c: IConnectionOperation) => c.config.type)
				: null),
		total_tries: connectionsOperation[0]?.config?.total_tries || 3,
		uuid: connectionsOperation[0]?.uuid || choose?.uuid,
	};
}

/** Doc con conexión financiera recomendada (shape mínimo del form DOCUMENTS). */
export type DocWithRecommendedConnection = {
	docInfo?: {
		confRecommended?: {
			connection_available?: boolean;
			connection_type?: CONNECTION_TYPE;
		};
	};
};

/**
 * Docs cuya conexión recomendada ya está seleccionada en CONNECTIONS (conflicto UI).
 *
 * @param docs - Filas DOCUMENTS disponibles
 * @param connections - Tipos seleccionados en CONNECTIONS
 */
export function findDuplicateFinancialConnections<T extends DocWithRecommendedConnection>(
	docs: T[] | null | undefined,
	connections: CONNECTION_TYPE[] | null | undefined,
): T[] {
	return (
		docs?.filter(
			(doc) =>
				doc.docInfo?.confRecommended?.connection_available &&
				connections?.includes(doc.docInfo?.confRecommended?.connection_type as CONNECTION_TYPE),
		) ?? []
	);
}

/**
 * ¿Deshabilitar un tipo de evaluación financiera en el panel CONNECTIONS?
 * (misma regla legacy: conflicto con docs / ya hay conexiones incompatibles)
 *
 * @param type - Tipo a evaluar
 * @param docs - Filas DOCUMENTS disponibles
 * @param connections - Tipos ya seleccionados
 */
export function isFinancialConnectionDisabled(
	type: CONNECTION_TYPE,
	docs: DocWithRecommendedConnection[] | null | undefined,
	connections: CONNECTION_TYPE[] | null | undefined,
): boolean {
	const duplicateConnection = findDuplicateFinancialConnections(docs, connections);
	const docsWithConnection =
		docs?.filter(
			(doc) =>
				doc.docInfo?.confRecommended?.connection_available &&
				doc.docInfo?.confRecommended?.connection_type === type,
		) ?? [];

	if (duplicateConnection?.length) {
		return Boolean(
			duplicateConnection.find((doc) => doc.docInfo?.confRecommended?.connection_type !== type),
		);
	}
	if (connections?.length && docsWithConnection.length) {
		return true;
	}
	return false;
}
